import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Crown, CalendarClock, RefreshCw, Receipt, Infinity as InfinityIcon } from 'lucide-react';
import { useDashboard } from '@/context/DashboardContext';
import { dashboardApi } from '@/services/dashboardApi';
import { useSiteLang, dateLocale } from '@/components/site/SiteLang';

const PLAN_NAMES = { starter: 'Access', basica: 'PRO', premium: 'Premium' };

const fmtDate = (iso, lang) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(dateLocale(lang), { day: 'numeric', month: 'long', year: 'numeric' });
};

const T = {
  es: {
    opError: 'No se pudo completar la operación',
    title: 'Mi membresía',
    none: 'Sin membresía activa',
    lifetime: 'Vitalicia',
    activateBefore: 'Activa tu membresía desde ',
    home: 'Inicio',
    activateAfter: ' para acceder a la formación, herramientas y comisiones.',
    accessUntil: 'Acceso hasta',
    nextRenewal: 'Próxima renovación',
    billing: 'Facturación',
    company: 'Empresa o autónomo',
    individual: 'Particular',
    endOfPeriod: 'el final del período pagado',
    cancelledNote: (until) => `Has cancelado la renovación automática. Mantendrás el acceso hasta ${until} y no se te cobrará de nuevo.`,
    resumed: 'Renovación reactivada',
    resume: 'Reactivar renovación',
    confirmNote: (until) => `¿Seguro que quieres cancelar la renovación? Conservarás el acceso hasta ${until}; después la membresía dejará de estar activa.`,
    cancelled: 'Renovación cancelada',
    confirmCancel: 'Sí, cancelar renovación',
    keep: 'Mantener',
    cancelAuto: 'Cancelar renovación automática',
    lifetimeNote: 'Tu plaza no caduca ni se renueva: no hay cobros recurrentes.',
  },
  en: {
    opError: 'We couldn’t complete that. Please try again.',
    title: 'My membership',
    none: 'No active membership',
    lifetime: 'Lifetime',
    activateBefore: 'Activate your membership from ',
    home: 'Home',
    activateAfter: ' to unlock the training, tools and commissions.',
    accessUntil: 'Access until',
    nextRenewal: 'Next renewal',
    billing: 'Billing',
    company: 'Company or sole trader',
    individual: 'Individual',
    endOfPeriod: 'the end of the period you’ve paid for',
    cancelledNote: (until) => `You’ve cancelled automatic renewal. You’ll keep access until ${until} and won’t be charged again.`,
    resumed: 'Renewal reactivated',
    resume: 'Reactivate renewal',
    confirmNote: (until) => `Are you sure you want to cancel your renewal? You’ll keep access until ${until}; after that, your membership will no longer be active.`,
    cancelled: 'Renewal cancelled',
    confirmCancel: 'Yes, cancel renewal',
    keep: 'Keep it',
    cancelAuto: 'Cancel automatic renewal',
    lifetimeNote: 'Your membership never expires or renews: there are no recurring charges.',
  },
};

// Bloque "Mi membresía" del perfil: plan, renovación/caducidad y baja de la renovación.
export function MembershipStatusCard() {
  const { membership, refreshMembership } = useDashboard();
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const plan = membership?.active_membership;
  const expires = fmtDate(membership?.expires_at, lang);
  const cancelled = !!membership?.renewal_cancelled;
  // Vía de pago de la suscripción anual (particular / empresa); upgrades y vitalicia van por la misma
  const billingChannel = membership?.billing?.subscription_channel || null;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.message || okMsg);
      setConfirming(false);
      await refreshMembership?.();
    } catch (e) {
      toast.error(e?.message || t.opError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
      aria-labelledby="membership-status-title"
      data-testid="membership-status"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center">
            <Crown size={18} className="text-[#c9a84c]" />
          </div>
          <div>
            <h3 id="membership-status-title" className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{t.title}</h3>
            <p className="text-white text-lg font-semibold">{plan ? (PLAN_NAMES[plan] || plan) : t.none}</p>
          </div>
        </div>
        {plan && membership?.is_lifetime && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#c9a84c]/30 bg-[#c9a84c]/10 px-3 py-1 text-xs text-[#c9a84c]">
            <InfinityIcon size={13} /> {t.lifetime}
          </span>
        )}
      </div>

      {!plan && (
        <p className="mt-4 text-sm text-gray-400">
          {t.activateBefore}<Link to="/dashboard#membresias" className="text-[#c9a84c] underline underline-offset-2">{t.home}</Link>{t.activateAfter}
        </p>
      )}

      {plan && membership?.is_subscription && (
        <div className="mt-5 space-y-4">
          <div className="flex items-center gap-3 text-sm">
            <CalendarClock size={15} className="text-gray-400" />
            <span className="text-gray-400">{cancelled ? t.accessUntil : t.nextRenewal}</span>
            <span className="text-white tabular-nums">{expires || '—'}</span>
          </div>
          {billingChannel && (
            <div className="flex flex-wrap items-start gap-x-3 gap-y-1 text-sm" data-testid="membership-billing">
              <Receipt size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-400">{t.billing}</span>
              <span className="text-white">
                {billingChannel === 'empresa'
                  ? `${t.company} · ${membership?.billing?.subscription_entity || 'Inbrindor Tech, S.L.'}`
                  : t.individual}
              </span>
            </div>
          )}

          {cancelled ? (
            <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-4 text-sm text-amber-100/90">
              {t.cancelledNote(expires || t.endOfPeriod)}
              <div className="mt-3">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => dashboardApi.resumeSubscription(), t.resumed)}
                  className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/[0.06] disabled:opacity-50"
                  data-testid="membership-resume"
                >
                  <RefreshCw size={13} /> {t.resume}
                </button>
              </div>
            </div>
          ) : confirming ? (
            <div className="rounded-xl border border-red-400/20 bg-red-400/[0.05] p-4 text-sm text-gray-300" role="alert">
              {t.confirmNote(expires || t.endOfPeriod)}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => dashboardApi.cancelSubscription(), t.cancelled)}
                  className="rounded-lg bg-red-500/80 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-50"
                  data-testid="membership-cancel-confirm"
                >
                  {t.confirmCancel}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/[0.06]"
                >
                  {t.keep}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="text-xs text-gray-400 underline underline-offset-2 hover:text-white"
              data-testid="membership-cancel"
            >
              {t.cancelAuto}
            </button>
          )}
        </div>
      )}

      {plan && membership?.is_lifetime && (
        <p className="mt-4 text-sm text-gray-400">{t.lifetimeNote}</p>
      )}
    </motion.section>
  );
}

export default MembershipStatusCard;
